import { Component, Prop, Ref, SimpleEvent, Watch } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from '@/theme/Cart';
import { setVariantUrlParam } from '@/utils/products';
import { formatMoney } from '@/utils/formatting';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';
import { QuantitySelector } from '@/components/QuantitySelector/QuantitySelector';

/* eslint-disable @typescript-eslint/naming-convention, @typescript-eslint/no-namespace */
declare global {
    namespace Shopify {
        namespace Liquid {
            interface Variant {
                unit_price?: number | undefined;
                unit_price_measurement: {
                    measured_type: 'volume' | 'weight' | 'dimension';
                    quantity_unit: string;
                    quantity_value: number;
                    reference_unit: string;
                    reference_value: number;
                };
            }
        }
    }
}

@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop defaultVariant!: Shopify.Liquid.Variant;
    @Prop optionStyle!: 'dropdown'|'buttons';
    @Prop strings!: Translations;
    @Prop parentCategory!: string;
    @Prop linkedProductName!: string;
    @Prop swatchColour!: string;

    @Ref({required: false}) quantitySelector!: QuantitySelector;
    @Ref({required: false}) options!: ProductOptionPicker;
    @Ref({required: false}) actions!: HTMLDivElement;
    @Ref({required: false}) submitButton!: HTMLButtonElement;
    @Ref({required: false}) error!: HTMLElement;
    @Ref({required: false}) errorMessage!: HTMLElement;
    @Ref({required: false}) dynamicCheckout!: HTMLElement;
    @Ref({required: false}) variantInput!: HTMLInputElement;

    protected loading: boolean = false;
    protected selectedVariant: Shopify.Liquid.Variant|null = null;
    protected quantity: number = 1;

    public productOptionPicker = this.querySelector('product-option-picker') as ProductOptionPicker;
    private stockAlert: boolean = false;

    init() {
        // Set default variant
        this.selectedVariant = this.defaultVariant;

        // Check variant availability
        this.checkVariantAvailability(this.selectedVariant);

        // Listen for quantity updates
        if (this.quantitySelector) {
            this.$subscribe(this.quantitySelector.onChange, (quantity) => {

                // Hide error
                this.error.classList.remove('product-form__error--active');

                // Update quantity
                this.quantity = quantity;
            });
        }

        // Listen for variant updates
        if (this.options) {
            this.$subscribe(this.options.onSelectedVariantChange, (variant) => {
                if (variant === null) {
                    return;
                }
                this.selectedVariant = variant;

                // Check variant availability
                this.checkVariantAvailability(this.selectedVariant);

                this.checkStockAlert(this.selectedVariant);

                //Update hidden input
                this.variantInput.value = String(variant.id);

                // Update price
                const priceContainer = document.querySelector('.product-main-details__price--normal');
                const comparePrice:string|undefined = formatMoney(variant.compare_at_price) as string;
                const varPrice = formatMoney(variant.price) as string;

                if (priceContainer) {
                    if (variant.compare_at_price) {
                        priceContainer.textContent = comparePrice;

                    } else {
                        priceContainer.textContent = varPrice;
                    }
                }
                const finalPriceContainer = document.querySelector('.product-main-details__price--final');
                if (finalPriceContainer) {
                    if (variant.compare_at_price) {
                        finalPriceContainer.textContent = varPrice;
                        finalPriceContainer.classList.remove('visually-hidden');

                        if (priceContainer) {
                            priceContainer.classList.add('strikethrough');
                        }
                    } else {
                        finalPriceContainer.classList.add('visually-hidden');

                        if (priceContainer) {
                            priceContainer.classList.remove('strikethrough');
                        }
                    }
                }

                // Hide error
                this.error.classList.remove('product-form__error--active');
            });
            this.selectedVariant = this.options.selectedVariant;
        }

        // Listen for form submission
        if (this.submitButton) {
            const isSubscription = this.submitButton.dataset.subscription;
            const hasSellngPlan = (isSubscription === 'true');
            this.$listen(this.submitButton, 'click', (e) => {
                e.preventDefault();
                this.addToCart(hasSellngPlan).catch((e) => {
                    this.$log.error(`Add to bag failed`, { e });
                });
            });
        }
    }

    protected showError(msg: string|null): void {
        this.errorMessage.textContent = msg;
        if (msg) {
            this.error.classList.add('product-form__error--active');
        } else {
            this.error.classList.remove('product-form__error--active');
        }
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        if (!document.body.classList.contains('template-product')) { return; }
        setVariantUrlParam(this.selectedVariant?.id || null);
    }

    @Watch('loading')
    protected handleLoading() {
        if (this.submitButton) {
            this.submitButton.disabled = this.loading;
        }
    }

    private _onSelectedPropertyChange = new SimpleEvent<string>();
    public get onSelectedPropertyChange() {
        return this._onSelectedPropertyChange.asEvent();
    }

    checkVariantAvailability(variant: Shopify.Liquid.Variant|null) {
        // Check variant is defined
        if (!variant) {

            // Disable
            this.updateActionsVisibility(true, false);
            return;
        }

        // Check variant is in stock
        if (!variant.available) {

            // Disable
            this.updateActionsVisibility(true, true);
            return;
        }

        // Enable
        this.updateActionsVisibility(false, false);
        this.unitPrice(variant);
    }

    checkStockAlert(variant: Shopify.Liquid.Variant|null) {
        // Check the variant inventory and if
        // the last remaining product is selected, we
        // can add a line item property when adding to cart

        // The same basic flow exists in the StockNotification component

        if (!variant) {
            this.stockAlert = false;
            return;
        }

        if (this.productOptionPicker && this.productOptionPicker.variantInventoryData) {
            const selectedItem = this.productOptionPicker.variantInventoryData
                .find((item) => {item.option == variant.title;});

            if (selectedItem && (selectedItem.quantity >= 2 || selectedItem.quantity == 0)) {
                this.stockAlert = false;
            } else if (selectedItem) {
                this.stockAlert = true;
            }
        }
    }

    updateActionsVisibility(disabled: boolean, soldOut: boolean) {
        if (disabled) {
            // Submit button
            if (this.submitButton) {

                // Disable button
                this.submitButton.classList.add('product-form__submit--disabled');
            }

            if (this.dynamicCheckout) {
                this.dynamicCheckout.classList.remove('is-active');
            }

            if (soldOut) {
                this.submitButton.innerText = this.strings.sold_out;
                this.submitButton.classList.add('product-form__submit--soldout');
            }
        } else {

            // Submit button
            if (this.submitButton) {

                this.submitButton.innerText = this.strings.add_to_cart;

                // Enable button
                this.submitButton.classList.remove('product-form__submit--disabled');
                this.submitButton.classList.remove('product-form__submit--soldout');
            }

            if (this.dynamicCheckout) {
                this.dynamicCheckout.classList.add('is-active');
            }

            // Show actions
            // if (this.actions) {
            //     this.actions.classList.remove('product-form__actions--hidden');
            // }
        }
    }

    unitPrice(variant:Shopify.Liquid.Variant) {
        const unitPriceContainer = this.parentElement?.parentElement?.querySelector('[data-ref="unitPriceContainer"]');
        if (!unitPriceContainer) { return; }
        if (!variant.unit_price) {
            unitPriceContainer.textContent = '';
            return;
        }

        const measurement = variant.unit_price_measurement;
        const reference = measurement.reference_value;
        const baseUnit =  `${reference > 1? reference :''}${measurement.reference_unit}`;
        const unitPrice = formatMoney(variant.unit_price) as string;

        unitPriceContainer.textContent = `(${unitPrice} / ${baseUnit})`;
    }

    /**
     * Get selling plan frequency.
     * @param variant - Shopify variants.
     * @param planId - selling plan id
     * @returns
     */
    getPlanFrequency(variant:Shopify.Liquid.Variant, planId:number) {
        const sellingPlans: Shopify.Liquid.SellingPlan[] =
            this.product.selling_plan_groups[0].selling_plans as Shopify.Liquid.SellingPlan[];
        for (const plan of sellingPlans) {
            if (Number(plan.id) === Number(planId)) {
                return plan.name;
            }
        }

        return null;
    }

    /**
     * Add to bag
     * @param hasSellngPlan - variant has a selling plan.
     * @returns
     */
    protected async addToCart(hasSellngPlan:boolean) {
        try {
            this.loading = true;

            // Check variant exists
            const variant = this.selectedVariant;
            if (!variant) {
                this.showError(`Please select an option.`);
                return;
            }

            // Define line item properties shape
            let properties: {[key: string]: string} = {};
            let selling_plan:number|undefined;

            if (hasSellngPlan) {
                const planInput = this.querySelector('[name="selling_plan"]') as HTMLInputElement;
                selling_plan = Number(planInput?.value) as number;
                const frequency = this.getPlanFrequency(variant, selling_plan) as string;

                if (frequency != null) {
                    /* eslint-disable */
                    properties = {
                        '_selling_plan_frequency': frequency
                    };
                    /* eslint-enable */
                }
            }

            // Add metafield values as line item properties
            // so that they can be present in cart and the order
            if (this.parentCategory) {
                properties['_parent-category'] = this.parentCategory;
            }

            if (this.linkedProductName) {
                properties['_linked-product-name'] = this.linkedProductName;
            }

            if (this.swatchColour) {
                properties['_swatch-colour'] = this.swatchColour;
            }

            // If its the last available product in the selected size
            // add a property for displaying in cart
            if (this.stockAlert) {
                properties['_stock-alert'] = 'true';
            }

            // Add to bag
            await cart.addItem({
                id: variant.id,
                quantity: this.quantity,
                properties: properties,
                // eslint-disable-next-line
                selling_plan: selling_plan
            });

            // Dispatch event for other components to listen to
            document.dispatchEvent(new Event('cartOpen'));
        } catch (event) {
            this.$log.error('Cart error', {
                event
            });
            const { message } = event as Error;
            this.showError(message);
        } finally {
            this.loading = false;
        }
    }
}
